import React, { useState, useEffect, useRef } from 'react';
import { Grid, Card, Box, CardContent, Typography, MenuItem, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Ministry from '../ministry';
import LGD from '../lgd';
import Event from '../event';
import axios from 'axios';
import { useSelector } from 'react-redux';
import TableComponent from '../../components/table';
import {
  getAllEventsByQuery,
  getAllEventsByQueryOther,
  getAllEventTheme,
} from '../../firebase/functions';
import { getPresignedUrlRead } from '../../api';
import Spinner from '../../views/spinner/Spinner';
import CustomFormLabel from '../forms/custom-elements/CustomFormLabel';
import CustomSelect from '../forms/custom-elements/CustomSelect';
import { isNationalScopeRole } from '../../constants/roles';
import { getCtuReport2025, getEventTypeReport, getEventDetails } from '../../api';
import {
  isApiReportYear,
  isReportEventTheme,
  getThemeOptions,
  getEventTypeReportColumns,
} from '../../constants/reportYears';

const LocationWiseReport = ({ setIsLoading, year }) => {
  // The API years default to 'all' (CTU & Clean Public Spaces); 2024 leaves the
  // theme unset and takes it from the Event dropdown instead.
  const [selectedEventThemeCode, setSelectedEventThemeCode] = useState(
    isApiReportYear(year) ? 'all' : undefined
  );
  const [selectedEventNameCode, setSelectedEventNameCode] = useState(undefined);
  const [selectedEventHeadingCode, setSelectedEventHeadingCode] = useState(undefined);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState(undefined);
  const [selectedDistrictCode, setSelectedDistrictCode] = useState(undefined);
  const [selectedBlockCode, setSelectedBlockCode] = useState(undefined);
  const [selectedGPCode, setSelectedGPCode] = useState(undefined);
  const [selectedVillageCode, setSelectedVillageCode] = useState(undefined);
  const [selectedULBCode, setSelectedULBCode] = useState(undefined);
  const [ministryCode, setMinistryCode] = useState(undefined);
  const [ministryDepartmentCode, setMinistryDepartmentCode] = useState(undefined);
  const [ministrySubUnitCode, setMinistrySubUnitCode] = useState(undefined);
  const [selectedCtuStatus, setSelectedCtuStatus] = useState(undefined);
  const [selectedAreaType, setSelectedAreaType] = useState(undefined);
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState({});

  // Only the newest report fetch may write to the table.
  //
  // getReports / getReports2025 / getEventTypeReports2025 each clear the table,
  // await, then write. With no cancellation, two overlapping runs -- a filter
  // changed mid-request, View Report pressed twice, a tab switched -- resolve in
  // whatever order the network returns them, and the LAST to land wins. That is
  // how a request that genuinely returned rows ends up showing "No data yet":
  // an older request finished afterwards and took the empty branch. The same
  // race silently shows a previous filter's numbers when the older response
  // happens to be non-empty, which is harder to notice.
  const latestReportFetchRef = useRef(0);
  const beginReportFetch = () => {
    latestReportFetchRef.current += 1;
    return latestReportFetchRef.current;
  };
  const isStaleReport = (ticket) => latestReportFetchRef.current !== ticket;
  const role = useSelector((state) => state.RoleReducer);
  const [filters, setFilters] = useState([]);

  // Area Types from add-event form
  const areaTypes = [
    'Abandon or open land with dumping',
    'Area near Transfer Station',
    'Back lanes',
    'Cleaning of Water Tank',
    'Construction Debris Areas',
    'Drainage, Nallahs',
    'Garbage Dump',
    'Market spaces, Mandis',
    'Railway tracks',
    'Roadsides (NH, SH, Local)',
    'Slums',
    'Waterbodies',
  ];
  const [events, setEvents] = useState([]);
  const [ctuEvents, setCtuEvents] = useState([]);
  const [cpsEvents, setCpsEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const pageSize = 100; // Keep original for reports
  const cardPageSize = 25; // New size for CTU/CPS event cards
  const [lastDoc, setLastDoc] = useState(null);
  const [ctuLastDoc, setCtuLastDoc] = useState(null);
  const [cpsLastDoc, setCpsLastDoc] = useState(null);
  const [ctuPageCursors, setCtuPageCursors] = useState({ 0: null });
  const [cpsPageCursors, setCpsPageCursors] = useState({ 0: null });
  const [ctuPage, setCtuPage] = useState(0);
  const [cpsPage, setCpsPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ctuLoading, setCtuLoading] = useState(false);
  const [cpsLoading, setCpsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  // Handler for Event Theme Name change - clears other filters
  // Function to process images for events using getEventDetails API
  const processEventImages = async (events) => {
    const processedEvents = await Promise.all(
      events.map(async (event) => {
        let beforeImage = null;
        let afterImage = null;

        const eventId = event.event_id || event.doc_id;

        try {
          // Get detailed event data by ID (same as view-event component).
          // Report Events themes -- the 2025 trio and the SHS-2026 pillars --
          // are stored in report_events rather than events.
          const isThemeSpecificEvent = isReportEventTheme(event?.event_theme_id);
          const eventDetails = await getEventDetails(
            eventId,
            isThemeSpecificEvent ? 'report_events' : null
          );

          if (eventDetails?.data?.media && eventDetails.data.media.length > 0) {
            // Process each media item to get presigned URLs (same logic as view-event)
            const mediaPromises = eventDetails.data.media.map(async (mediaItem) => {
              try {
                const media = mediaItem?.media_url;
                if (!media) return null;

                // Extract folder name and file name
                const folderName = media.substring(1, media.lastIndexOf('/'));
                const fileName = media.substring(media.lastIndexOf('/') + 1);

                const presignedUrl = await getPresignedUrlRead(fileName, folderName);

                return {
                  url: presignedUrl?.data,
                  type: mediaItem?.event_media_type,
                };
              } catch (error) {
                return null;
              }
            });

            const processedMedia = await Promise.all(mediaPromises);
            const validMedia = processedMedia.filter(Boolean);

            // Find before and after images
            beforeImage =
              validMedia.find((media) => media.type === 'BEFORE' || media.type === 'before')?.url ||
              null;

            afterImage =
              validMedia.find((media) => media.type === 'AFTER' || media.type === 'after')?.url ||
              null;
          }
        } catch (error) {
          // Silent error handling
        }

        return {
          ...event,
          before_image: beforeImage,
          after_image: afterImage,
        };
      })
    );

    return processedEvents;
  };

  const handleEventThemeChange = (e) => {
    const newEventThemeCode = e.target.value;
    setSelectedEventThemeCode(newEventThemeCode);

    // Clear Area Type when switching away from CTU & CPS
    if (newEventThemeCode !== 'all') {
      setSelectedAreaType(undefined);
    }

    // // Clear ministry filters
    // setMinistryCode(undefined);
    // setMinistryDepartmentCode(undefined);
    // setMinistrySubUnitCode(undefined);

    // // Clear location filters
    // setSelectedLocation('');
    // setSelectedStateCode(undefined);
    // setSelectedDistrictCode(undefined);
    // setSelectedBlockCode(undefined);
    // setSelectedGPCode(undefined);
    // setSelectedVillageCode(undefined);
    // setSelectedULBCode(undefined);

    // Clear events data
    setEvents([]);
    setCtuEvents([]);
    setCpsEvents([]);
    setLastDoc(null);
    setCtuLastDoc(null);
    setCpsLastDoc(null);
    setCtuPageCursors({ 0: null });
    setCpsPageCursors({ 0: null });
    setCtuPage(0);
    setCpsPage(0);
  };

  useEffect(() => {
    getFilteredData(true);
  }, [filters]);

  // The theme dropdown only exists on the API years; 2024 filters by the Event
  // theme/category/type dropdowns instead. Both years' option lists are built
  // by getThemeOptions -- see the note there.
  const getEventTheme = async () => {
    if (!isApiReportYear(year)) return;

    try {
      const types = await getAllEventTheme();
      setEventTypes(getThemeOptions(year, types));
    } catch (error) {
      console.error('Error fetching event themes:', error);
    }
  };

  useEffect(() => {
    getEventTheme();
  }, [year]); // Include year in dependency to re-run when year changes

  // Reset selectedEventThemeCode when year changes
  useEffect(() => {
    if (isApiReportYear(year)) {
      setSelectedEventThemeCode('all');
    } else {
      setSelectedEventThemeCode(undefined);
    }
  }, [year]);

  const getCtuData = async (targetPage = 0) => {
    if (Object.keys(filters).length > 0 && selectedEventThemeCode === 'all') {
      setCtuLoading(true);
      const baseFilters = { ...filters };
      delete baseFilters.event_theme_id;

      const ctuFilters = { ...baseFilters, event_name_id: 34 };
      const cursor = ctuPageCursors[targetPage] || null;

      try {
        const ctuResult = await getAllEventsByQuery(cursor, cardPageSize, ctuFilters);
        if (ctuResult) {
          const processedCtuEvents = await processEventImages(ctuResult.data);
          setCtuEvents(processedCtuEvents);
          setCtuLastDoc(ctuResult.lastDoc);

          if (ctuResult.lastDoc && !ctuPageCursors[targetPage + 1]) {
            setCtuPageCursors((prev) => ({ ...prev, [targetPage + 1]: ctuResult.lastDoc }));
          }
        }
      } catch (error) {
        console.error('Error fetching CTU events:', error);
      }
      setCtuLoading(false);
    }
  };

  const getCpsData = async (targetPage = 0) => {
    if (Object.keys(filters).length > 0 && selectedEventThemeCode === 'all') {
      setCpsLoading(true);
      const baseFilters = { ...filters };
      delete baseFilters.event_theme_id;

      const cpsFilters = { ...baseFilters, event_theme_id: 7 };
      const cursor = cpsPageCursors[targetPage] || null;

      try {
        const cpsResult = await getAllEventsByQuery(
          cursor,
          cardPageSize,
          cpsFilters,
          undefined,
          true
        );
        if (cpsResult) {
          setCpsEvents(cpsResult.data);
          setCpsLastDoc(cpsResult.lastDoc);

          if (cpsResult.lastDoc && !cpsPageCursors[targetPage + 1]) {
            setCpsPageCursors((prev) => ({ ...prev, [targetPage + 1]: cpsResult.lastDoc }));
          }
        }
      } catch (error) {
        console.error('Error fetching CPS events:', error);
      }
      setCpsLoading(false);
    }
  };

  const getFilteredData = async (resetData = false) => {
    if (Object.keys(filters).length > 0) {
      // Check if we need to make two separate calls for 'all' (CTU & Clean Public Spaces)
      if (selectedEventThemeCode === 'all') {
        // Create base filters without event_theme_id
        const baseFilters = { ...filters };
        delete baseFilters.event_theme_id;

        // Start both loading states
        setCtuLoading(true);
        setCpsLoading(true);

        // First call for CTU events (event_name_id = 34) - parallel
        const ctuFilters = { ...baseFilters, event_name_id: 34 };
        const ctuCursor = resetData ? null : ctuPageCursors[ctuPage] || null;
        getAllEventsByQuery(ctuCursor, cardPageSize, ctuFilters)
          .then(async (ctuResult) => {
            if (ctuResult) {
              console.log(ctuResult, 'CTU filtered events');

              // Process images for CTU events
              const processedCtuEvents = await processEventImages(ctuResult.data);

              setCtuEvents(processedCtuEvents);
              setCtuLastDoc(ctuResult.lastDoc);

              // Store cursor for next page if we have a newLastDoc and don't already have cursor for next page
              if (ctuResult.lastDoc && !ctuPageCursors[ctuPage + 1]) {
                setCtuPageCursors((prev) => ({ ...prev, [ctuPage + 1]: ctuResult.lastDoc }));
              }
            }
            setCtuLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching CTU events:', error);
            setCtuLoading(false);
          });

        // Second call for Clean Public Spaces (event_theme_id = 7) - parallel
        const cpsFilters = { ...baseFilters, event_theme_id: 7 };
        const cpsCursor = resetData ? null : cpsPageCursors[cpsPage] || null;
        getAllEventsByQuery(cpsCursor, cardPageSize, cpsFilters, undefined, true)
          .then((cpsResult) => {
            if (cpsResult) {
              setCpsEvents(cpsResult.data);
              setCpsLastDoc(cpsResult.lastDoc);

              // Store cursor for next page if we have a newLastDoc and don't already have cursor for next page
              if (cpsResult.lastDoc && !cpsPageCursors[cpsPage + 1]) {
                setCpsPageCursors((prev) => ({ ...prev, [cpsPage + 1]: cpsResult.lastDoc }));
              }
            }
            setCpsLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching CPS events:', error);
            setCpsLoading(false);
          });
      } else {
        // Normal single call for specific event themes
        setLoading(true);

        // Use different query function based on event theme ID. Every Report
        // Events theme -- the 2025 trio and the SHS-2026 pillars alike -- lives
        // in report_events, which only getAllEventsByQueryOther reads.
        const queryFunction = isReportEventTheme(selectedEventThemeCode)
          ? getAllEventsByQueryOther
          : getAllEventsByQuery;

        const filteredEvents = await queryFunction(resetData ? null : lastDoc, pageSize, filters);
        if (filteredEvents) {
          console.log(filteredEvents, 'filtered events');

          // Process images for regular events
          const processedEvents = await processEventImages(filteredEvents.data);

          if (resetData) {
            setEvents(processedEvents);
          } else {
            const uniqueEvents = [
              ...new Map(
                [...processedEvents, ...events].map((event) => [event.doc_id, event])
              ).values(),
            ];
            setEvents(uniqueEvents);
          }
          setLastDoc(filteredEvents.lastDoc);
        }
        setLoading(false);
      }
    }
  };

  const filterEvents = async () => {
    const newFilters = {};
    if (selectedEventThemeCode) newFilters.event_theme_id = selectedEventThemeCode;
    if (selectedEventNameCode) newFilters.event_name_id = selectedEventNameCode;
    if (selectedEventHeadingCode) newFilters.event_heading_id = selectedEventHeadingCode;
    if (selectedLocation) newFilters.location_type = selectedLocation;
    if (selectedStateCode) newFilters.state_code = selectedStateCode;
    if (selectedDistrictCode) newFilters.district_code = selectedDistrictCode;
    if (selectedBlockCode) newFilters.block_code = selectedBlockCode;
    if (selectedGPCode) newFilters.gp_code = selectedGPCode;
    if (selectedVillageCode) newFilters.village_code = selectedVillageCode;
    if (selectedULBCode) newFilters.ulb_code = selectedULBCode;
    if (ministryCode) newFilters.ministry_code = ministryCode;
    if (ministryDepartmentCode) newFilters.ministry_department_code = ministryDepartmentCode;
    if (ministrySubUnitCode) newFilters.ministry_unit_code = ministrySubUnitCode;
    if (selectedCtuStatus !== undefined) newFilters.event_completed = selectedCtuStatus;
    if (isApiReportYear(year)) newFilters.event_year = String(year);
    setLastDoc(null);
    setCtuLastDoc(null);
    setCpsLastDoc(null);
    setCtuPageCursors({ 0: null });
    setCpsPageCursors({ 0: null });
    setCtuPage(0);
    setCpsPage(0);
    setFilters(newFilters);
  };

  useEffect(() => {
    if (selectedEventNameCode === 34 || isApiReportYear(year)) {
      if (selectedVillageCode || selectedULBCode) {
        filterEvents();
      }
    }
  }, [
    ministryCode,
    ministryDepartmentCode,
    ministrySubUnitCode,
    selectedEventNameCode,
    selectedVillageCode,
    selectedULBCode,
    selectedCtuStatus,
  ]);

  const allRow = {
    view_event: 'View Event',
    before_image: 'Before Image',
    after_image: 'After Image',
    event_user_name: 'CTU Name',
    location_type: 'Location Type',
    state_name: 'State Name',
    district_name: 'District Name',
    ulb_name: 'ULB Name',
    block_name: 'Block Name',
    gp_name: 'GP Name',
    ministry_name: 'Ministry Name',
    ministry_department_name: 'Ministry Department Name',
    ministry_unit_name: 'Ministry Unit Department Name',
    village_name: 'Village Name',
    event_start_date: 'Event Date (YYYY-MM-DD)',
    event_coordinator_name: 'Event Coordinator',
    event_completed: 'Completed',
  };

  const downloadRow = {
    event_theme_name: 'Event Theme Name',
    event_heading_name: 'Event Category',
    event_name: 'Event Type',
    event_user_name: 'CTU Name',
    location_type: 'Location Type',
    state_name: 'State Name',
    district_name: 'District Name',
    ulb_name: 'ULB Name',
    block_name: 'Block Name',
    gp_name: 'GP Name',
    village_name: 'Village Name',
    ministry_name: 'Ministry Name',
    ministry_department_name: 'Ministry Department Name',
    ministry_unit_name: 'Ministry Unit Department Name',
    event_start_date: 'Event Date (YYYY-MM-DD)',
    event_coordinator_name: 'Event Coordinator',
    event_completed: 'Completed',
  };

  // Function to filter headers based on event theme
  const getFilteredHeaders = (headers, eventThemeCode, hideCpsImages = false) => {
    // report_events rows carry none of these per-event columns, whatever the
    // campaign year.
    const shouldHideColumns = isReportEventTheme(eventThemeCode);
    const filteredHeaders = { ...headers };

    if (shouldHideColumns) {
      const columnsToHide = [
        'event_user_name',
        'event_coordinator_name',
        'event_start_date',
        'event_completed',
      ];
      columnsToHide.forEach((column) => {
        delete filteredHeaders[column];
      });
    }

    // Hide image columns for CPS events and change CTU Name to Event Name
    if (hideCpsImages) {
      delete filteredHeaders.before_image;
      delete filteredHeaders.after_image;
      if (filteredHeaders.event_user_name) {
        filteredHeaders.event_user_name = 'Event Name';
      }
    }

    return filteredHeaders;
  };

  const roleRow = {
    ctu_action: 'CTU Action',
    view_event: 'View Event',
    close_event: 'Close Event',
    update_daily_event: 'Update Daily Event',
    event_user_name: 'CTU Name',
    location_type: 'Location Type',
    state_name: 'State Name',
    district_name: 'District Name',
    ulb_name: 'ULB Name',
    block_name: 'Block Name',
    gp_name: 'GP Name',
    ministry_name: 'Ministry Name',
    ministry_department_name: 'Ministry Department Name',
    ministry_unit_name: 'Ministry Unit Department Name',
    village_name: 'Village Name',
    event_start_date: 'Event Date (YYYY-MM-DD)',
    event_coordinator_name: 'Event Coordinator',
    before_image: 'Before Image',
    after_image: 'After Image',
    event_completed: 'Completed',
  };

  const getReports = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    const baseUrl = `https://asia-south1-dashakswachhataka.cloudfunctions.net/getReportsLatest?resource=events&labels=["total_count"]&modifiers=["count"]&rank_by=state_name`;

    let filters = [`state_code != ''`];
    let categories = [];
    let allHeaders = {};
    let fields = [
      '*',
      `SUM(CASE WHEN event_theme_id = '1' THEN 1 ELSE 0 END) AS Swachhata_Mein_Jan_Bhaagidari`,
      `SUM(CASE WHEN event_theme_id = '2' THEN 1 ELSE 0 END) AS Sampoorna_Swachhata_Swachhata_Lakshit_Ekayi`,
      `SUM(CASE WHEN event_theme_id = '3' THEN 1 ELSE 0 END) AS SafaiMitra_Suraksha_Shivirs`,
      `SUM(CASE WHEN event_name_id = '34' THEN 1 ELSE 0 END) AS CTU_Identified`,
      `SUM(CASE WHEN event_name_id = '34' AND event_completed = TRUE THEN 1 ELSE 0 END) AS CTU_Transformed`,
      `SUM(CASE WHEN event_name_id = '34' AND event_completed = FALSE THEN 1 ELSE 0 END) AS CTU_Pending`,
      `SUM(CASE WHEN event_type = 'PARTNER_ADOPT_CTU' THEN 1 ELSE 0 END) AS CTU_Adopted`,
      `SUM(CASE WHEN event_type = 'PARTNER_JOIN_CTU' THEN 1 ELSE 0 END) AS CTU_Joined`,
    ];
    let clickhouse_fields = [
      '*',
      `SUM(if(event_theme_id = '1', 1, 0)) AS Swachhata_Mein_Jan_Bhaagidari`,
      `SUM(if(event_theme_id = '2', 1, 0)) AS Sampoorna_Swachhata_Swachhata_Lakshit_Ekayi`,
      `SUM(if(event_theme_id = '3', 1, 0)) AS SafaiMitra_Suraksha_Shivirs`,
      `SUM(if(event_name_id = '34', 1, 0)) AS CTU_Identified`,
      ` SUM(
    IF(
      (event_name_id = '34') AND (event_completed = 'true'),
      1,
      0
    )
  ) AS CTU_Transformed`,
      ` SUM(
    IF(
      (event_name_id = '34') AND (event_completed = 'false'),
      1,
      0
    )
  ) AS CTU_Pending`,
      `SUM(if(event_type = 'PARTNER_ADOPT_CTU', 1, 0)) AS CTU_Adopted`,
      `SUM(if(event_type = 'PARTNER_JOIN_CTU', 1, 0)) AS CTU_Joined`,
    ];

    if (selectedEventThemeCode) {
      filters.push(`event_theme_id='${selectedEventThemeCode}'`);
    }

    if (selectedEventHeadingCode) {
      filters.push(`event_heading_id='${selectedEventHeadingCode}'`);
    }

    if (selectedEventNameCode) {
      filters.push(`event_name_id='${selectedEventNameCode}'`);
    }

    if (selectedLocation) {
      filters.push(`location_type='${selectedLocation}'`);
      categories.push('location_type');
      categories.push('state_name');
      categories.push('state_code');
      fields.push('location_type');
      fields.push('state_name');
      fields.push('state_code');
      clickhouse_fields.push('location_type');
      clickhouse_fields.push('state_name');
      clickhouse_fields.push('state_code');
      allHeaders = {
        ...allHeaders,
        location_type: 'Location Name',
        state_name: 'State Name',
      };
    } else {
      categories.push('state_name');
      categories.push('state_code');
      fields.push('state_name');
      fields.push('state_code');
      clickhouse_fields.push('state_name');
      clickhouse_fields.push('state_code');
      allHeaders = {
        ...allHeaders,
        state_name: 'State Name',
      };
    }

    if (selectedStateCode) {
      filters.push(`state_code='${selectedStateCode}'`);
      categories.push('district_name');
      categories.push('district_code');
      fields.push('district_code');
      fields.push('district_name');
      clickhouse_fields.push('district_code');
      clickhouse_fields.push('district_name');
      allHeaders = {
        ...allHeaders,
        district_name: 'District Name',
      };
    }

    if (selectedDistrictCode) {
      filters.push(`district_code='${selectedDistrictCode}'`);
      if (selectedLocation === 'RURAL') {
        categories.push('block_name');
        categories.push('block_code');
        fields.push('block_code');
        fields.push('block_name');
        clickhouse_fields.push('block_code');
        clickhouse_fields.push('block_name');
        allHeaders = {
          ...allHeaders,
          block_name: 'Block Name',
        };
      } else {
        categories.push('ulb_name');
        categories.push('ulb_code');
        fields.push('ulb_code');
        fields.push('ulb_name');
        clickhouse_fields.push('ulb_code');
        clickhouse_fields.push('ulb_name');
        allHeaders = {
          ...allHeaders,
          ulb_name: 'ULB Name',
        };
      }
    }

    if (selectedBlockCode) {
      filters.push(`block_code='${selectedBlockCode}'`);
      categories.push('gp_name');
      categories.push('gp_code');
      fields.push('gp_code');
      fields.push('gp_name');
      clickhouse_fields.push('gp_code');
      clickhouse_fields.push('gp_name');
      allHeaders = {
        ...allHeaders,
        gp_name: 'GP Name',
      };
    }

    if (selectedULBCode) {
      filters.push(`ulb_code='${selectedULBCode}'`);
      allHeaders = {
        ...allHeaders,
        block_name: 'ULB Name',
      };
    }

    if (selectedGPCode) {
      filters.push(`gp_code='${selectedGPCode}'`);
      categories.push('village_name');
      categories.push('village_code');
      fields.push('village_code');
      fields.push('village_name');
      clickhouse_fields.push('village_code');
      clickhouse_fields.push('village_name');
      allHeaders = {
        ...allHeaders,
        village_name: 'Village Name',
      };
    }

    if (selectedVillageCode) {
      filters.push(`village_code='${selectedVillageCode}'`);
    }

    if (ministryCode) {
      filters.push(`ministry_code='${ministryCode}'`);
    }

    if (ministryDepartmentCode) {
      filters.push(`ministry_department_code='${ministryDepartmentCode}'`);
    }

    if (ministrySubUnitCode) {
      filters.push(`ministry_unit_code='${ministrySubUnitCode}'`);
    }

    let query = baseUrl;
    if (filters.length > 0) {
      query = query + `&filters=[${filters.map((f) => `"${f}"`).join(',')}]`;
    }
    if (categories.length > 0) {
      query = query + `&categories=[${categories.map((f) => `"${f}"`).join(',')}]`;
    }
    if (fields.length > 0) {
      query = query + `&fields=[${fields.map((f) => `"${f}"`).join(',')}]`;
    }
    if (clickhouse_fields.length > 0) {
      query = query + `&clickhouse_fields=[${clickhouse_fields.map((f) => `"${f}"`).join(',')}]`;
    }
    allHeaders = {
      ...allHeaders,
      total_count: 'Total Events',
      CTU_Identified: 'CTU Identified',
      CTU_Transformed: 'CTU Transformed',
      CTU_Pending: 'Open CTU',
      SafaiMitra_Suraksha_Shivirs: 'SafaiMitra Suraksha Shivirs',
      Swachhata_Mein_Jan_Bhaagidari: 'Jan Bhagidari Activities',
    };
    try {
      // const url = `getCachedResultV1?url=${encodeURIComponent(query)}`;
      const response = await axios.get(query);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response.data) {
        // Process data to replace empty state information
        const processedData = response.data.map((row) => {
          // Check if state_code is empty string, "0", null, undefined or state_name is empty, null, undefined, or "-"
          const isEmptyStateCode =
            !row.state_code ||
            row.state_code === '' ||
            row.state_code === '0' ||
            row.state_code === 0;
          const isEmptyStateName =
            !row.state_name || row.state_name === '' || row.state_name === '-';

          if (isEmptyStateCode && isEmptyStateName) {
            return {
              ...row,
              state_name: 'Ministry of External Affairs (Outside India)',
            };
          }
          return row;
        });
        setTableData(processedData);
        setHeaders(allHeaders);
      } else {
        setTableData([]);
        setHeaders({});
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      if (isStaleReport(reportTicket)) return;
      setIsLoading(false);
    }
  };

  const getEventTypeReports2025 = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    try {
      // Build filters object for Event Type Report API call
      const filters = {
        report_type: 'location_wise',
        event_theme_id: selectedEventThemeCode,
        // Without this the API falls back to its default year (2025), so a
        // 2026 request would silently return 2025 numbers.
        event_year: String(year),
      };
      if (selectedLocation) filters.location_type = selectedLocation;
      if (selectedStateCode) filters.state_code = selectedStateCode;
      if (selectedDistrictCode) filters.district_code = selectedDistrictCode;
      if (selectedULBCode) filters.ulb_code = selectedULBCode;
      if (selectedBlockCode) filters.block_code = selectedBlockCode;
      if (selectedGPCode) filters.gp_code = selectedGPCode;
      if (selectedVillageCode) filters.village_code = selectedVillageCode;
      if (ministryCode) filters.ministry_code = ministryCode;
      if (ministryDepartmentCode) filters.ministry_department_code = ministryDepartmentCode;
      if (ministrySubUnitCode) filters.ministry_unit_code = ministrySubUnitCode;

      // Pass filters to the Event Type Report API function
      const response = await getEventTypeReport(filters);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response?.data?.data) {
        // Set dynamic headers based on the response data structure with proper column order
        let headers = {
          state_name: 'State Name',
        };

        // Add district and ulb columns first (maintaining location hierarchy order)
        if (selectedStateCode && selectedLocation === 'URBAN') {
          headers.district_name = 'District Name';
          headers.ulb_name = 'ULB Name';
        }
        if (selectedStateCode && selectedLocation === 'RURAL') {
          headers.district_name = 'District Name';
        }
        if (selectedDistrictCode && selectedLocation === 'RURAL') {
          headers.block_name = 'Block Name';
        }
        if (selectedBlockCode && selectedLocation === 'RURAL') {
          headers.gp_name = 'GP Name';
        }
        if (selectedGPCode && selectedLocation === 'RURAL') {
          headers.village_name = 'Village Name';
        }

        // Themes with a fixed column list for this campaign year (the SHS-2026
        // pillars) are laid out from that list instead of from the response
        // keys, so the order and the wording come from the KPI document rather
        // than from whatever the query happened to SELECT. They also skip the
        // derived Total columns below, which are specific to the 2025 themes.
        const pillarColumns = getEventTypeReportColumns(year, selectedEventThemeCode);

        // Add special columns for Advocacy theme (event_theme_id = 6) after location columns
        if (!pillarColumns && (selectedEventThemeCode === 6 || selectedEventThemeCode === '6')) {
          headers.total_events_conducted = 'Total events conducted';
          headers.total_participants_sum = 'Total Participants';
        }

        // Add special column for Clean Green Utsav theme (event_theme_id = 5) after location columns
        if (selectedEventThemeCode === 5 || selectedEventThemeCode === '5') {
          headers.total_events_conducted = 'Total events conducted';
        }

        // Special handling for SafaiMitra theme (event_theme_id = 3) - custom column order
        let isShiviriTheme = selectedEventThemeCode === 3 || selectedEventThemeCode === '3';

        // Don't add ministry column since user already knows which ministry they selected from dropdown

        // Process data to add calculated columns for specific themes
        let processedData = response.data.data || [];

        if (pillarColumns) {
          // Nothing to derive: every pillar column is reported as-is.
        } else if (selectedEventThemeCode === 6 || selectedEventThemeCode === '6') {
          // Advocacy theme processing
          processedData = processedData.map((row) => {
            // Calculate total events conducted by summing all event-related numeric fields
            let totalEventsKeys = Object.keys(row).filter(
              (key) =>
                key.includes('number_of_') &&
                !key.includes('participants') &&
                !key.includes('males') &&
                !key.includes('females') &&
                typeof row[key] === 'number'
            );

            const totalEvents = totalEventsKeys.reduce(
              (sum, key) => sum + (parseInt(row[key]) || 0),
              0
            );

            // Calculate total participants by summing participant-related fields
            let participantKeys = Object.keys(row).filter(
              (key) =>
                (key.includes('participants') || key.includes('total_participants')) &&
                typeof row[key] === 'number'
            );

            const totalParticipants = participantKeys.reduce(
              (sum, key) => sum + (parseInt(row[key]) || 0),
              0
            );

            return {
              ...row,
              total_events_conducted: totalEvents,
              total_participants_sum: totalParticipants,
            };
          });
        } else if (selectedEventThemeCode === 5 || selectedEventThemeCode === '5') {
          // Clean Green Utsav theme processing
          processedData = processedData.map((row) => {
            // Calculate total events conducted by summing the 3 specific columns
            const ecoPandal = parseInt(row.total_number_of_eco_pandal) || 0;
            const rangolis = parseInt(row.total_number_of_swachhata_rangolis) || 0;
            const cleanlinessDrives =
              parseInt(row.total_number_of_post_festive_cleanliness_drives) || 0;

            const totalEvents = ecoPandal + rangolis + cleanlinessDrives;

            return {
              ...row,
              total_events_conducted: totalEvents,
            };
          });
        } else if (selectedEventThemeCode === 3 || selectedEventThemeCode === '3') {
          // SafaiMitra theme processing - add sum column for 2025
          processedData = processedData.map((row) => {
            // Calculate sum of no_of_sanitation_workers_recognition + health_participants + no_ppe_kits_safety_gear_distributed + total_beneficiaries
            const sanitationWorkersRecognition =
              parseInt(row.no_of_sanitation_workers_recognition) || 0;
            const healthParticipants = parseInt(row.health_participants) || 0;
            const ppeKitsDistributed = parseInt(row.no_ppe_kits_safety_gear_distributed) || 0;
            const totalBeneficiaries = parseInt(row.total_beneficiaries) || 0;

            const totalHealthPpeBeneficiaries =
              sanitationWorkersRecognition +
              healthParticipants +
              ppeKitsDistributed +
              totalBeneficiaries;

            return {
              ...row,
              total_health_ppe_beneficiaries: totalHealthPpeBeneficiaries,
            };
          });
        }

        // Handle headers differently for SafaiMitra theme vs other themes
        if (pillarColumns) {
          headers = { ...headers, ...pillarColumns };
        } else if (response.data.data && response.data.data.length > 0) {
          const firstRow = response.data.data[0];

          if (isShiviriTheme) {
            // Custom column order for SafaiMitra theme (event_theme_id = 3)
            const shiviriColumns = [
              { key: 'total_number_of_shivirs_conducted', label: 'Shivirs Conducted' },
              {
                key: 'total_sanitation_workers_participated',
                label: 'Sanitation workers participated',
              },
              {
                key: 'no_of_sanitation_workers_recognition',
                label: 'Sanitation workers recognized',
              },
              {
                key: 'preventive_health_checkup_camps_set',
                label: 'Preventative health checkups organized',
              },
              {
                key: 'no_of_preventive_health_checkups_organized',
                label: 'Preventive health checkups organized',
              },
              { key: 'health_participants', label: 'Health checkup participants' },
              {
                key: 'ppe_kits_safety_gear_distributed',
                label: 'No of ppe kits safety gear distributed',
              },
              {
                key: 'no_ppe_kits_safety_gear_distributed',
                label: 'PPE kits distributed',
              },
              // { key: 'ppe_participants', label: 'PPE participants' },
              { key: 'total_beneficiaries', label: 'Scheme Beneficiaries' },
              { key: 'total_health_ppe_beneficiaries', label: 'Total Beneficiaries' },
            ];

            // Add only the specified columns in the specified order
            shiviriColumns.forEach((column) => {
              if (
                firstRow.hasOwnProperty(column.key) ||
                column.key === 'total_health_ppe_beneficiaries'
              ) {
                headers[column.key] = column.label;
              }
            });
          } else {
            // Default dynamic header creation for other themes
            Object.keys(firstRow).forEach((key) => {
              if (
                ![
                  'state_name',
                  'state_code',
                  'district_name',
                  'district_code',
                  'ulb_name',
                  'ulb_code',
                  'block_name',
                  'block_code',
                  'gp_name',
                  'gp_code',
                  'village_name',
                  'village_code',
                  'ministry_name',
                  'ministry_code',
                  'ministry_department_code',
                  'ministry_unit_code',
                  'total_events_conducted',
                  'total_participants_sum',
                ].includes(key)
              ) {
                // Convert snake_case to Title Case for display
                const displayName = key
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                headers[key] = displayName;
              }
            });
          }
        }

        setHeaders(headers);

        // Process data to replace empty state information
        processedData = processedData.map((row) => {
          // Check if state_code is empty string, "0", null, undefined or state_name is empty, null, undefined, or "-"
          const isEmptyStateCode =
            !row.state_code ||
            row.state_code === '' ||
            row.state_code === '0' ||
            row.state_code === 0;
          const isEmptyStateName =
            !row.state_name || row.state_name === '' || row.state_name === '-';

          if (isEmptyStateCode && isEmptyStateName) {
            return {
              ...row,
              state_name: 'Ministry of External Affairs (Outside India)',
            };
          }
          return row;
        });

        // Add total row for Event Type reports
        if (processedData.length > 0) {
          const totalRow = {};

          // Add "Total" label in the first column
          const firstColumn = Object.keys(headers)[0];
          totalRow[firstColumn] = 'Total';

          // Calculate totals for numeric columns, display '-' for string columns
          Object.keys(headers).forEach((column) => {
            if (column !== firstColumn) {
              // Skip the first column (state_name)
              // Check if this is a location column (string values)
              const locationColumns = [
                'district_name',
                'ulb_name',
                'block_name',
                'gp_name',
                'village_name',
              ];

              if (locationColumns.includes(column)) {
                // Display '-' for location/string columns
                totalRow[column] = '-';
              } else {
                // Calculate totals for numeric columns
                const total = processedData.reduce((sum, row) => {
                  const value = row[column];
                  return sum + (typeof value === 'number' ? value : parseInt(value) || 0);
                }, 0);
                totalRow[column] = total;
              }
            }
          });

          setTableData([...processedData, totalRow]);
        } else {
          setTableData(processedData);
        }
      } else {
        setTableData([]);
        setHeaders({});
      }
    } catch (error) {
      console.error('Error fetching Event Type report:', error);
      if (isStaleReport(reportTicket)) return;
      setTableData([]);
      setHeaders({});
    }
    setIsLoading(false);
  };

  const getReports2025 = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    try {
      // Build filters object for API call
      const filters = {
        report_type: 'location_wise',
        // See the note in getEventTypeReports2025: the API defaults to 2025.
        event_year: String(year),
      };
      if (selectedLocation) filters.location_type = selectedLocation;
      if (selectedStateCode) filters.state_code = selectedStateCode;
      if (selectedDistrictCode) filters.district_code = selectedDistrictCode;
      if (selectedULBCode) filters.ulb_code = selectedULBCode;
      if (selectedBlockCode) filters.block_code = selectedBlockCode;
      if (selectedGPCode) filters.gp_code = selectedGPCode;
      if (selectedVillageCode) filters.village_code = selectedVillageCode;
      if (ministryCode) filters.ministry_code = ministryCode;
      if (ministryDepartmentCode) filters.ministry_department_code = ministryDepartmentCode;
      if (ministrySubUnitCode) filters.ministry_unit_code = ministrySubUnitCode;
      if (selectedAreaType) filters.area_type = selectedAreaType;

      // Pass filters to the API function
      const response = await getCtuReport2025(filters);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response?.data) {
        // Set headers based on whether state is selected or not
        let headers = {
          state_name: 'State Name',
          ctu_total_events: 'CTU Identified',
          ctu_completed_events: 'CTU Transformed',
          ctu_pending_events: 'Open CTU',
          low_intensity: 'Low Intensity CTU',
          medium_intensity: 'Medium Intensity CTU',
          high_intensity: 'High Intensity CTU',
          ctu_total_participants: 'CTU Participants',
          theme7_total_events: 'Clean Public Spaces Identified',
          theme7_completed_events: 'Public Spaces Cleaned',
          theme7_pending_events: 'Pending Public Spaces',
          theme7_total_participants: 'Clean Public Spaces Participants',
        };

        // Only show district_name and ulb_name when state is selected
        if (selectedStateCode && selectedLocation === 'URBAN') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            ulb_name: 'ULB Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedStateCode && selectedLocation === 'RURAL') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }
        if (selectedDistrictCode && selectedLocation === 'RURAL') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            block_name: 'Block Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedBlockCode && selectedLocation === 'RURAL') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            block_name: 'Block Name',
            gp_name: 'GP Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedGPCode && selectedLocation === 'RURAL') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            block_name: 'Block Name',
            gp_name: 'GP Name',
            village_name: 'Village Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedVillageCode && selectedLocation === 'RURAL') {
          headers = {
            state_name: 'State Name',
            district_name: 'District Name',
            block_name: 'Block Name',
            gp_name: 'GP Name',
            village_name: 'Village Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        // Don't add ministry column since user already knows which ministry they selected from dropdown

        // 2026 drops the CTU intensity breakdown. The three columns are still
        // built in every header variant above and still returned by the API --
        // they are removed here, in one place, rather than edited out of each of
        // the seven variants, so 2025 and 2024 are untouched and putting them back
        // is a matter of deleting this block.
        //
        // Order matters: this runs after the variant selection and before
        // setHeaders, so it catches whichever variant was chosen. The totals row
        // below is keyed on `headers[column]`, so dropping the header also drops
        // the column from the Total, and TableComponent derives its export columns
        // from this same object.
        if (Number(year) === 2026) {
          delete headers.low_intensity;
          delete headers.medium_intensity;
          delete headers.high_intensity;
        }

        setHeaders(headers);

        // Process data to replace empty state information
        let processedData = response.data.data.map((row) => {
          // Check if state_code is empty string, "0", null, undefined or state_name is empty, null, undefined, or "-"
          const isEmptyStateCode =
            !row.state_code ||
            row.state_code === '' ||
            row.state_code === '0' ||
            row.state_code === 0;
          const isEmptyStateName =
            !row.state_name || row.state_name === '' || row.state_name === '-';

          if (isEmptyStateCode && isEmptyStateName) {
            return {
              ...row,
              state_name: 'Ministry of External Affairs (Outside India)',
            };
          }
          return row;
        });

        // Add total row only on the API years
        if (isApiReportYear(year) && processedData.length > 0) {
          const numericalColumns = [
            'ctu_total_events',
            'ctu_completed_events',
            'ctu_pending_events',
            'low_intensity', // Add low intensity
            'medium_intensity', // Add medium intensity
            'high_intensity', // Add high intensity
            'ctu_total_participants', // Add CTU participants
            'theme7_total_events',
            'theme7_completed_events',
            'theme7_pending_events',
            'theme7_total_participants', // Add Clean Public Spaces participants
          ];
          const totalRow = {};

          // Add "Total" label in the first column
          const firstColumn = Object.keys(headers)[0];
          totalRow[firstColumn] = 'Total';

          // Calculate totals for numerical columns
          numericalColumns.forEach((column) => {
            if (headers[column]) {
              const total = processedData.reduce(
                (sum, row) => sum + (parseInt(row[column]) || 0),
                0
              );
              totalRow[column] = total;
            }
          });

          setTableData([...processedData, totalRow]);
        } else {
          setTableData(processedData);
        }
      } else {
        setTableData([]);
        setHeaders({});
      }
    } catch (error) {
      console.error('Error fetching 2025 CTU report:', error);
      if (isStaleReport(reportTicket)) return;
      setTableData([]);
      setHeaders({});
    }
    setIsLoading(false);
  };

  // Helper function to decide which API to call. Both 2025-named helpers take
  // the campaign year from the `year` prop and serve every API year.
  const getReports2025Handler = () => {
    if (!isApiReportYear(year)) {
      // Call 2024 reports
      getReports();
    } else if (selectedEventThemeCode && selectedEventThemeCode !== 'all') {
      // Call Event Type Report API for specific event themes
      getEventTypeReports2025();
    } else {
      // Call original CTU Report API for 'all' or default
      getReports2025();
    }
  };

  // Handle View Report button click
  const handleViewReport = async () => {
    setReportLoading(true);
    try {
      // Call the appropriate report function based on role and selected filters
      if (!role?.info?.role_id || isNationalScopeRole(role?.info?.role_id)) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 2 && ministryCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 3 && ministryDepartmentCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 4 && ministrySubUnitCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 5 && selectedStateCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 6 && selectedDistrictCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 7 && selectedULBCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 8 && selectedBlockCode) {
        await getReports2025Handler();
      } else {
        // If conditions not met, still allow report generation
        await getReports2025Handler();
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Load initial default data only once when component mounts
  useEffect(() => {
    if (!role?.info?.role_id || isNationalScopeRole(role?.info?.role_id)) {
      getReports2025Handler();
    }
  }, [role?.info?.role_id, year]);
  return (
    <Grid container spacing={2} mb={2} mt={2}>
      {/* Event Theme Name Dropdown - Only show for year 2025 */}
      {isApiReportYear(year) && eventTypes?.length > 0 && (
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Event Theme Name</CustomFormLabel>
          <CustomSelect
            value={selectedEventThemeCode || ''}
            onChange={handleEventThemeChange}
            id="event_theme_id"
            fullWidth
            name="event_theme_id"
            size="small"
          >
            {eventTypes.map((data) => (
              <MenuItem value={data.value} key={data.value}>
                {data.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}

      <Ministry
        ministryCode={ministryCode}
        setMinistryCode={setMinistryCode}
        ministryDepartmentCode={ministryDepartmentCode}
        setMinistryDepartmentCode={setMinistryDepartmentCode}
        ministrySubUnitCode={ministrySubUnitCode}
        setMinistrySubUnitCode={setMinistrySubUnitCode}
      />

      <LGD
        eventYear={year}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        selectedStateCode={selectedStateCode}
        setSelectedStateCode={setSelectedStateCode}
        selectedDistrictCode={selectedDistrictCode}
        setSelectedDistrictCode={setSelectedDistrictCode}
        selectedBlockCode={selectedBlockCode}
        setSelectedBlockCode={setSelectedBlockCode}
        selectedULBCode={selectedULBCode}
        setSelectedULBCode={setSelectedULBCode}
        selectedGPCode={selectedGPCode}
        setSelectedGPCode={setSelectedGPCode}
        selectedVillageCode={selectedVillageCode}
        setSelectedVillageCode={setSelectedVillageCode}
      />

      {/* Hide Event component on the API years -- their theme dropdown above
          replaces it. */}
      {!isApiReportYear(year) && (
        <Event
          selectedEventThemeCode={selectedEventThemeCode}
          setSelectedEventThemeCode={setSelectedEventThemeCode}
          selectedEventNameCode={selectedEventNameCode}
          setSelectedEventNameCode={setSelectedEventNameCode}
          selectedEventHeadingCode={selectedEventHeadingCode}
          setSelectedEventHeadingCode={setSelectedEventHeadingCode}
        />
      )}

      {(selectedULBCode || selectedVillageCode) && (
        <Grid item xs={12} md={3}>
          <CustomFormLabel>CTU Status</CustomFormLabel>
          <CustomSelect
            value={selectedCtuStatus !== undefined ? selectedCtuStatus : ''}
            onChange={(e) =>
              setSelectedCtuStatus(e.target.value === '' ? undefined : e.target.value === 'true')
            }
            id="ctu_status"
            fullWidth
            name="ctu_status"
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">CTU Transformed</MenuItem>
            <MenuItem value="false">Open CTU</MenuItem>
          </CustomSelect>
        </Grid>
      )}

      {/* Area Type dropdown - only visible for admin users (role_id: 1) and when CTU & CPS is selected */}
      {isNationalScopeRole(role?.info?.role_id) &&
        selectedEventThemeCode === 'all' &&
        isApiReportYear(year) && (
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Area Type</CustomFormLabel>
            <CustomSelect
              value={selectedAreaType || ''}
              onChange={(e) =>
                setSelectedAreaType(e.target.value === '' ? undefined : e.target.value)
              }
              id="area_type"
              fullWidth
              name="area_type"
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {areaTypes.map((areaType, index) => (
                <MenuItem key={index} value={areaType}>
                  {areaType}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
        )}

      {/* View Report Button */}
      {isApiReportYear(year) && (
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleViewReport}
            loading={reportLoading}
            size="large"
            sx={{ minWidth: 200 }}
          >
            View Report
          </LoadingButton>
        </Grid>
      )}

      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box
            sx={{
              width: '100%',
              overflow: 'auto',
            }}
          >
            {tableData?.length > 0 ? (
              <TableComponent
                data={tableData}
                mainHeaders={headers}
                fileName={'reports'}
                year={year} // Add this line to pass the year prop
                showPagination={false} // Disable pagination for reports
              />
            ) : (
              <Typography variant="h3">No data yet</Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Show separate cards for CTU and CPS when 'all' is selected */}
      {selectedEventThemeCode === 'all' &&
        isApiReportYear(year) &&
        (selectedULBCode || selectedVillageCode) && (
          <>
            {/* CTU Events Card */}
            <Card sx={{ width: '100%', mb: 2 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                  CTU Events
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'auto',
                  }}
                >
                  {ctuLoading ? (
                    <Spinner />
                  ) : (
                    <>
                      {ctuEvents.length > 0 ? (
                        <TableComponent
                          mainHeaders={getFilteredHeaders(
                            role?.info?.role_id === 10 ? roleRow : allRow,
                            selectedEventThemeCode
                          )}
                          data={ctuEvents}
                          hideExportButton={true}
                          fileName={'CTU Events Details'}
                          downloadRow={downloadRow}
                          serverSidePagination={true}
                          currentPage={ctuPage}
                          getData={() => {
                            setCtuLastDoc(null);
                            setCtuPageCursors({ 0: null });
                            setCtuPage(0);
                            getCtuData(0);
                          }}
                          isPublic
                          handleNext={(newPage) => {
                            setCtuPage(newPage);
                            getCtuData(newPage);
                          }}
                          useNewExportApi={true}
                          eventType="ctu"
                        />
                      ) : (
                        <Typography variant="h6">No CTU events yet</Typography>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Clean Public Spaces Events Card */}
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Clean Public Spaces Events
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'auto',
                  }}
                >
                  {cpsLoading ? (
                    <Spinner />
                  ) : (
                    <>
                      {cpsEvents.length > 0 ? (
                        <TableComponent
                          mainHeaders={getFilteredHeaders(
                            role?.info?.role_id === 10 ? roleRow : allRow,
                            selectedEventThemeCode,
                            true
                          )}
                          hideExportButton={true}
                          data={cpsEvents}
                          fileName={'Clean Public Spaces Events Details'}
                          downloadRow={downloadRow}
                          serverSidePagination={true}
                          currentPage={cpsPage}
                          getData={() => {
                            setCpsLastDoc(null);
                            setCpsPageCursors({ 0: null });
                            setCpsPage(0);
                            getCpsData(0);
                          }}
                          isPublic
                          handleNext={(newPage) => {
                            setCpsPage(newPage);
                            getCpsData(newPage);
                          }}
                          useNewExportApi={true}
                          eventType="cps"
                        />
                      ) : (
                        <Typography variant="h6">No Clean Public Spaces events yet</Typography>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </>
        )}

      {/* Original single card for other event themes or non-2025 years */}
      {((selectedEventNameCode === 34 && !isApiReportYear(year)) ||
        (isApiReportYear(year) && selectedEventThemeCode && selectedEventThemeCode !== 'all')) &&
        (selectedULBCode || selectedVillageCode) && (
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    {events.length > 0 ? (
                      <TableComponent
                        mainHeaders={getFilteredHeaders(
                          role?.info?.role_id === 10 ? roleRow : allRow,
                          selectedEventThemeCode
                        )}
                        data={events}
                        fileName={'Event Details'}
                        downloadRow={downloadRow}
                        getData={() => {}}
                        isPublic
                        handleNext={() => {
                          if (Object.keys(filters).length > 0) {
                            getFilteredData(false);
                          } else {
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="h3">No data yet</Typography>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
    </Grid>
  );
};

export default LocationWiseReport;
